import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Copy,
  Edit3,
  Heart,
  Info,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Share2,
  Trash2,
  Trophy,
  X,
} from 'lucide-react';
import { api } from '../utils/client';
import { requestNotificationsRefresh } from '../utils/utils';
import type {
  Artifact,
  ArtifactCreateEditResponse,
  ArtifactVisual,
  Blueprint,
  GenerateArtifactResponse,
  RecipeCombinationWithArtifacts,
} from '../types/blueprints';

import {
  recipeTabActiveClass,
  recipeTabInactiveClass,
} from './RecipeCardTabControls';
import DropdownMenu, { MenuItem } from './DropdownMenu';
import { useCaps } from '../contexts/CapabilitiesContext';

interface Props {
  blueprintId: number;
  initialCombinationId?: number;
  initialArtifactId?: number;
  onBack?: () => void;
  /** Renders inline inside the recipe workflow instead of a full-screen overlay. */
  embedded?: boolean;
  /** When set, combination selection is controlled by the parent (e.g. shared tab row). */
  combinationId?: number | null;
  hideCombinationTabs?: boolean;
  hidePageHeader?: boolean;
  /** Parent reports whether every recipe attribute has a selected option. */
  combinationAttributesComplete?: boolean;
  onOpenProductIdeaGenerator?: () => void;
  onOpenProductIdeaValidator?: () => void;
}

const formatArtifactIndexLabel = (index: number) =>
  `Concept ${String(index + 1).padStart(2, '0')}`;

const getArtifactDisplayName = (artifact: Artifact, index: number) =>
  artifact.name?.trim() || formatArtifactIndexLabel(index);

type ArtifactMenuTarget = { artifact: Artifact; index: number };

const getSortedVisuals = (artifact: Artifact | null): ArtifactVisual[] =>
  [...(artifact?.visuals ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

const getPmfColor = (label: string | undefined | null): string => {
  const normalized = (label || '').trim().toLowerCase();
  if (normalized === 'strong') return '#00B050';
  if (normalized === 'mid') return '#FAA61A';
  if (normalized === 'weak') return '#FF0000';
  return '#9CA3AF';
};

const VISIBLE_THUMBNAIL_COUNT = 5;
const THUMBNAIL_SIZE_PX = 100;
const THUMBNAIL_GAP_PX = 8;
const autoCreatingFirstIdea = new Set<number>();

const primaryTabActive =
  'bg-white border border-gray-200 border-b-white text-gray-900 font-medium rounded-t-lg -mb-px z-10';
const primaryTabInactive =
  'bg-gray-100 border border-transparent text-gray-600 rounded-t-lg hover:bg-gray-50 hover:text-gray-900 hover:border-gray-200';

const ConceptGenerationPage: React.FC<Props> = ({
  blueprintId,
  initialCombinationId,
  initialArtifactId,
  onBack,
  embedded = false,
  combinationId: controlledCombinationId,
  hideCombinationTabs = false,
  hidePageHeader = false,
  combinationAttributesComplete = true,
  onOpenProductIdeaGenerator,
  onOpenProductIdeaValidator,
}) => {
  const { can } = useCaps();
  const canAiGenerate = can('ai_generate');
  const canEditBlueprints = can('blueprints_edit');
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);
  const [combinations, setCombinations] = useState<RecipeCombinationWithArtifacts[]>([]);
  const [activeCombinationId, setActiveCombinationId] = useState<number | null>(
    initialCombinationId ?? null
  );
  const [activeArtifactId, setActiveArtifactId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingWritten, setIsGeneratingWritten] = useState(false);
  const [isManualAddMode, setIsManualAddMode] = useState(false);
  const [manualAddContent, setManualAddContent] = useState('');
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusInput, setStatusInput] = useState('');
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [isCreatingBlankArtifact, setIsCreatingBlankArtifact] = useState(false);
  const [showGenerateConfirmModal, setShowGenerateConfirmModal] = useState(false);
  const [renameCombinationTarget, setRenameCombinationTarget] =
    useState<RecipeCombinationWithArtifacts | null>(null);
  const [renameCombinationValue, setRenameCombinationValue] = useState('');
  const [isRenamingCombination, setIsRenamingCombination] = useState(false);
  const [deleteCombinationTarget, setDeleteCombinationTarget] =
    useState<RecipeCombinationWithArtifacts | null>(null);
  const [isDeletingCombination, setIsDeletingCombination] = useState(false);
  const [renameArtifactTarget, setRenameArtifactTarget] = useState<ArtifactMenuTarget | null>(null);
  const [renameArtifactValue, setRenameArtifactValue] = useState('');
  const [isRenamingArtifact, setIsRenamingArtifact] = useState(false);
  const [deleteArtifactTarget, setDeleteArtifactTarget] = useState<ArtifactMenuTarget | null>(null);
  const [isDeletingArtifact, setIsDeletingArtifact] = useState(false);
  const [selectedVisualId, setSelectedVisualId] = useState<number | null>(null);
  const [isSettingPrimaryVisual, setIsSettingPrimaryVisual] = useState(false);
  const [isTogglingFavoriteVisual, setIsTogglingFavoriteVisual] = useState(false);
  const [deleteVisualTarget, setDeleteVisualTarget] = useState<ArtifactVisual | null>(null);
  const [isDeletingVisual, setIsDeletingVisual] = useState(false);
  const [visualThumbnailOffset, setVisualThumbnailOffset] = useState(0);
  const [showVisualEditPanel, setShowVisualEditPanel] = useState(false);
  const [visualEditRequest, setVisualEditRequest] = useState('');
  const [isApplyingVisualEdit, setIsApplyingVisualEdit] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const [comboMenuOpen, setComboMenuOpen] = useState<number | null>(null);
  const [artifactMenuOpen, setArtifactMenuOpen] = useState<number | null>(null);
  const [visualMenuOpen, setVisualMenuOpen] = useState(false);

  const comboMenuRef = useRef<HTMLButtonElement>(null);
  const artifactMenuRef = useRef<HTMLButtonElement>(null);
  const visualMenuRef = useRef<HTMLButtonElement>(null);
  const statusMenuContainerRef = useRef<HTMLDivElement>(null);

  const activeCombination = useMemo(
    () => combinations.find((c) => c.id === activeCombinationId) ?? null,
    [combinations, activeCombinationId]
  );

  const orderedArtifacts = useMemo(() => {
    if (!activeCombination?.artifacts?.length) return [];
    return [...activeCombination.artifacts].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [activeCombination]);

  const activeArtifact = useMemo(
    () => orderedArtifacts.find((a) => a.id === activeArtifactId) ?? null,
    [orderedArtifacts, activeArtifactId]
  );
  const activeArtifactVisuals = useMemo(() => getSortedVisuals(activeArtifact), [activeArtifact]);
  const selectedVisual = useMemo(() => {
    if (!activeArtifact) return null;
    const byId = activeArtifactVisuals.find((v) => v.id === selectedVisualId);
    if (byId) return byId;
    return activeArtifactVisuals.find((v) => v.is_primary) ?? activeArtifactVisuals[0] ?? null;
  }, [activeArtifact, activeArtifactVisuals, selectedVisualId]);
  const selectedVisualUrl = selectedVisual?.image_url || activeArtifact?.visual_concept || '';
  const isVisualActionBusy =
    isSettingPrimaryVisual ||
    isTogglingFavoriteVisual ||
    isDeletingVisual ||
    isRegenerating ||
    isApplyingVisualEdit;
  const showVisualScrollArrows = activeArtifactVisuals.length > VISIBLE_THUMBNAIL_COUNT;
  const showArtifactTabs = Boolean(activeCombination);
  const combinationReady = combinationAttributesComplete;
  const canGenerateConcepts = combinationReady && canAiGenerate && canEditBlueprints;
  const isArtifactBlank =
    Boolean(activeArtifact) &&
    !activeArtifact?.written_concept?.trim() &&
    activeArtifactVisuals.length === 0;
  const usedArtifactStatuses = useMemo(() => {
    const statuses = new Set<string>();
    combinations.forEach((combo) => {
      combo.artifacts?.forEach((artifact) => {
        const trimmed = artifact.status?.trim();
        if (trimmed) statuses.add(trimmed);
      });
    });
    return Array.from(statuses);
  }, [combinations]);
  const pmfScoreDisplay =
    selectedVisual?.pmf_score != null ? String(selectedVisual.pmf_score) : '-';
  const pmfLabelDisplay = selectedVisual?.pmf_label?.trim() || '-';
  const pmfColor = getPmfColor(selectedVisual?.pmf_label);
  const hasVisual =
    activeArtifactVisuals.length > 0 || Boolean(activeArtifact?.visual_concept?.trim());
  const maxVisualThumbnailOffset = Math.max(
    0,
    activeArtifactVisuals.length - VISIBLE_THUMBNAIL_COUNT
  );
  const canScrollVisualsLeft = visualThumbnailOffset > 0;
  const canScrollVisualsRight = visualThumbnailOffset < maxVisualThumbnailOffset;
  const thumbnailViewportWidth =
    VISIBLE_THUMBNAIL_COUNT * THUMBNAIL_SIZE_PX +
    (VISIBLE_THUMBNAIL_COUNT - 1) * THUMBNAIL_GAP_PX;

  const methodologyFlags = activeArtifact?.methodology_flags ?? [];

  const selectionPayload = useMemo(() => {
    if (!activeCombination?.selections?.length) return [];
    return activeCombination.selections.map((s) => ({
      attribute_set: s.attribute_set,
      option: s.option,
    }));
  }, [activeCombination]);

  const selectionSummary = useMemo(() => {
    if (!selectionPayload.length) return '';
    const lines = selectionPayload.map(
      (s) => `- attribute_set=${s.attribute_set}, option=${s.option}`
    );
    return `Selected recipe options:\n${lines.join('\n')}`;
  }, [selectionPayload]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [bp, combos] = await Promise.all([
        api<Blueprint>(`/api/blueprints/${blueprintId}/`),
        api<RecipeCombinationWithArtifacts[]>(
          `/api/blueprints/${blueprintId}/recipe-combinations/`
        ),
      ]);
      setBlueprint(bp);
      setCombinations(combos);

      const comboId =
        initialCombinationId && combos.some((c) => c.id === initialCombinationId)
          ? initialCombinationId
          : combos[0]?.id ?? null;
      setActiveCombinationId(comboId);

      const combo = combos.find((c) => c.id === comboId);
      const artifacts = combo?.artifacts ?? [];
      const sorted = [...artifacts].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      const preferredArtifactId =
        initialArtifactId && sorted.some((a) => a.id === initialArtifactId)
          ? initialArtifactId
          : sorted[sorted.length - 1]?.id ?? null;
      setActiveArtifactId(preferredArtifactId);
    } catch (e: any) {
      setError(e.message || 'Failed to load concept generation data');
    } finally {
      setIsLoading(false);
    }
  }, [blueprintId, initialCombinationId, initialArtifactId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (controlledCombinationId == null) return;
    if (controlledCombinationId === activeCombinationId) return;
    if (!combinations.some((c) => c.id === controlledCombinationId)) return;
    setActiveCombinationId(controlledCombinationId);
    setIsEditMode(false);
    setComboMenuOpen(null);
    const combo = combinations.find((c) => c.id === controlledCombinationId);
    const sorted = [...(combo?.artifacts ?? [])].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    setActiveArtifactId(sorted[sorted.length - 1]?.id ?? null);
  }, [controlledCombinationId, combinations, activeCombinationId]);

  useEffect(() => {
    if (!activeCombination) {
      setActiveArtifactId(null);
      return;
    }
    const sorted = [...(activeCombination.artifacts ?? [])].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    if (!sorted.length) {
      setActiveArtifactId(null);
      return;
    }
    if (!sorted.some((a) => a.id === activeArtifactId)) {
      setActiveArtifactId(sorted[sorted.length - 1].id);
    }
  }, [activeCombination, activeArtifactId]);

  useEffect(() => {
    if (activeArtifact) {
      setEditedContent(activeArtifact.written_concept);
      setIsEditMode(false);
    }
    const visuals = getSortedVisuals(activeArtifact);
    const nextSelected = visuals.find((v) => v.is_primary)?.id ?? visuals[0]?.id ?? null;
    setSelectedVisualId(nextSelected);
    setVisualThumbnailOffset(0);
    setShowVisualEditPanel(false);
    setVisualEditRequest('');
    setVisualMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeArtifact?.id]);

  useEffect(() => {
    if (visualThumbnailOffset > maxVisualThumbnailOffset) {
      setVisualThumbnailOffset(maxVisualThumbnailOffset);
    }
  }, [visualThumbnailOffset, maxVisualThumbnailOffset]);

  useEffect(() => {
    if (!selectedVisualId || !showVisualScrollArrows) return;
    const index = activeArtifactVisuals.findIndex((v) => v.id === selectedVisualId);
    if (index < 0) return;
    setVisualThumbnailOffset((offset) => {
      if (index < offset) return index;
      if (index >= offset + VISIBLE_THUMBNAIL_COUNT) {
        return index - VISIBLE_THUMBNAIL_COUNT + 1;
      }
      return offset;
    });
  }, [selectedVisualId, activeArtifactVisuals, showVisualScrollArrows]);

  const updateCombinationArtifacts = (combinationId: number, artifacts: Artifact[]) => {
    setCombinations((prev) =>
      prev.map((c) => (c.id === combinationId ? { ...c, artifacts } : c))
    );
  };

  const handleCombinationChange = (combinationId: number) => {
    setActiveCombinationId(combinationId);
    setIsEditMode(false);
    setComboMenuOpen(null);
    const combo = combinations.find((c) => c.id === combinationId);
    const sorted = [...(combo?.artifacts ?? [])].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    setActiveArtifactId(sorted[sorted.length - 1]?.id ?? null);
  };

  const handleCreateBlankArtifact = useCallback(async () => {
    if (!activeCombination || !combinationReady || !canEditBlueprints) return;
    setError(null);
    setIsCreatingBlankArtifact(true);
    try {
      const created = await api<Artifact>(`/api/artifacts/create-blank/`, {
        method: 'POST',
        body: JSON.stringify({
          blueprint: blueprintId,
          combination_id: activeCombination.id,
        }),
      });
      const nextArtifacts = [...(activeCombination.artifacts ?? []), created];
      updateCombinationArtifacts(activeCombination.id, nextArtifacts);
      setActiveArtifactId(created.id);
      setIsEditMode(false);
      setIsManualAddMode(false);
      setManualAddContent('');
    } catch (e: any) {
      setError(e.message || 'Failed to create artifact');
    } finally {
      setIsCreatingBlankArtifact(false);
    }
  }, [
    activeCombination,
    combinationReady,
    canEditBlueprints,
    blueprintId,
  ]);

  useEffect(() => {
    if (isLoading || isCreatingBlankArtifact) return;
    if (!combinationReady || !canEditBlueprints) return;
    if (!activeCombination) return;
    if ((activeCombination.artifacts ?? []).length > 0) return;
    const comboId = activeCombination.id;
    if (autoCreatingFirstIdea.has(comboId)) return;
    autoCreatingFirstIdea.add(comboId);
    void handleCreateBlankArtifact().finally(() => {
      autoCreatingFirstIdea.delete(comboId);
    });
  }, [
    isLoading,
    isCreatingBlankArtifact,
    combinationReady,
    canEditBlueprints,
    activeCombination,
    handleCreateBlankArtifact,
  ]);

  const handleGenerateWritten = async () => {
    if (!activeArtifact?.id || !canGenerateConcepts) return;
    if (!canAiGenerate) {
      setError('You do not have permission to generate concepts');
      return;
    }
    setError(null);
    setIsGeneratingWritten(true);
    try {
      const response = await api<{ message: string; artifact: Artifact }>(
        `/api/artifacts/${activeArtifact.id}/generate-written/`,
        { method: 'POST' }
      );
      updateActiveArtifactFromResponse(response.artifact);
      setIsManualAddMode(false);
      requestNotificationsRefresh();
    } catch (e: any) {
      setError(e.message || 'Failed to generate written concept');
    } finally {
      setIsGeneratingWritten(false);
    }
  };

  const handleSaveManualWritten = async () => {
    if (!canEditBlueprints) {
      setError('You do not have permission to edit blueprints');
      return;
    }
    if (!activeArtifact?.id) return;
    const trimmed = manualAddContent.trim();
    if (!trimmed) return;
    setError(null);
    setIsSavingManual(true);
    try {
      const updated = await api<Artifact>(`/api/artifacts/${activeArtifact.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({ written_concept: trimmed }),
      });
      updateActiveArtifactFromResponse(updated);
      setIsManualAddMode(false);
      setManualAddContent('');
    } catch (e: any) {
      setError(e.message || 'Failed to save written concept');
    } finally {
      setIsSavingManual(false);
    }
  };

  const handleSaveStatus = async (nextStatus: string) => {
    if (!activeArtifact?.id) return;
    const trimmed = nextStatus.trim();
    setError(null);
    setIsSavingStatus(true);
    try {
      const updated = await api<Artifact>(`/api/artifacts/${activeArtifact.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({ status: trimmed }),
      });
      updateActiveArtifactFromResponse(updated);
      setStatusMenuOpen(false);
      setStatusInput('');
    } catch (e: any) {
      setError(e.message || 'Failed to save status');
    } finally {
      setIsSavingStatus(false);
    }
  };

  const closeStatusMenu = () => {
    setStatusMenuOpen(false);
    setStatusInput('');
  };

  useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!statusMenuContainerRef.current?.contains(event.target as Node)) {
        closeStatusMenu();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeStatusMenu();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [statusMenuOpen]);

  const handleGenerateVariation = async () => {
    if (!activeCombination) return;
    if (!canAiGenerate) {
      setError('You do not have permission to generate concepts');
      return;
    }
    setShowGenerateConfirmModal(false);
    setError(null);
    setIsGenerating(true);
    try {
      const response = await api<GenerateArtifactResponse>(
        `/api/blueprints/${blueprintId}/generate_artifact_v2/`,
        {
          method: 'POST',
          body: JSON.stringify({
            generate_visual: true,
            custom_prompt_additions: selectionSummary,
            selected_options: selectionPayload,
            combination_id: activeCombination.id,
          }),
        }
      );

      const nextArtifacts = [response.artifact, ...(activeCombination.artifacts ?? [])];
      updateCombinationArtifacts(activeCombination.id, nextArtifacts);
      setActiveArtifactId(response.artifact.id);
      setIsEditMode(false);
      requestNotificationsRefresh();
    } catch (e: any) {
      setError(e.message || 'Failed to generate concept');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!activeArtifact?.id) return;
    if (!canEditBlueprints) {
      setError('You do not have permission to edit blueprints');
      return;
    }
    setIsSavingEdit(true);
    setError(null);
    try {
      const response = await api<ArtifactCreateEditResponse>(
        `/api/artifacts/${activeArtifact.id}/create_edit/`,
        {
          method: 'POST',
          body: JSON.stringify({ written_concept: editedContent }),
        }
      );

      if (activeCombination) {
        const nextArtifacts = [...(activeCombination.artifacts ?? []), response.artifact];
        updateCombinationArtifacts(activeCombination.id, nextArtifacts);
        setActiveArtifactId(response.artifact.id);
      }
      setIsEditMode(false);
    } catch (e: any) {
      setError(e.message || 'Failed to save edited concept');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRegenerateVisual = async () => {
    if (!activeArtifact?.id) return;
    if (!canAiGenerate) {
      setError('You do not have permission to regenerate visuals');
      return;
    }
    setError(null);
    setIsRegenerating(true);
    setVisualMenuOpen(false);
    try {
      const response = await api<{ message: string; artifact: Artifact }>(
        `/api/artifacts/${activeArtifact.id}/regenerate_visual/`,
        { method: 'POST' }
      );

      if (activeCombination) {
        const newestVisualId = getSortedVisuals(response.artifact)[0]?.id ?? null;
        updateActiveArtifactFromResponse(response.artifact, newestVisualId);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate visual');
    } finally {
      setIsRegenerating(false);
    }
  };

  const openRenameArtifactModal = (artifact: Artifact, index: number) => {
    setArtifactMenuOpen(null);
    setRenameArtifactTarget({ artifact, index });
    setRenameArtifactValue(getArtifactDisplayName(artifact, index));
  };

  const closeRenameArtifactModal = () => {
    if (isRenamingArtifact) return;
    setRenameArtifactTarget(null);
    setRenameArtifactValue('');
  };

  const confirmRenameArtifact = async () => {
    const target = renameArtifactTarget;
    if (!target || !activeCombination) return;
    const trimmed = renameArtifactValue.trim();
    if (!trimmed || trimmed === getArtifactDisplayName(target.artifact, target.index)) return;
    setError(null);
    setIsRenamingArtifact(true);
    try {
      const updated = await api<Artifact>(`/api/artifacts/${target.artifact.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmed }),
      });
      const nextArtifacts = (activeCombination.artifacts ?? []).map((a) =>
        a.id === updated.id ? updated : a
      );
      updateCombinationArtifacts(activeCombination.id, nextArtifacts);
      setRenameArtifactTarget(null);
      setRenameArtifactValue('');
    } catch (e: any) {
      setError(e.message || 'Failed to rename artifact');
    } finally {
      setIsRenamingArtifact(false);
    }
  };

  const openDeleteArtifactModal = (artifact: Artifact, index: number) => {
    setArtifactMenuOpen(null);
    setDeleteArtifactTarget({ artifact, index });
  };

  const closeDeleteArtifactModal = () => {
    if (isDeletingArtifact) return;
    setDeleteArtifactTarget(null);
  };

  const confirmDeleteArtifact = async () => {
    const target = deleteArtifactTarget;
    if (!target?.artifact.id || !activeCombination) return;
    setError(null);
    setIsDeletingArtifact(true);
    try {
      await api(`/api/artifacts/${target.artifact.id}/`, { method: 'DELETE' });
      const nextArtifacts = (activeCombination.artifacts ?? []).filter(
        (a) => a.id !== target.artifact.id
      );
      updateCombinationArtifacts(activeCombination.id, nextArtifacts);
      const sorted = [...nextArtifacts].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setActiveArtifactId(sorted[sorted.length - 1]?.id ?? null);
      setDeleteArtifactTarget(null);
    } catch (e: any) {
      setError(e.message || 'Failed to delete artifact');
    } finally {
      setIsDeletingArtifact(false);
    }
  };

  const updateActiveArtifactFromResponse = (
    updated: Artifact,
    preferredVisualId?: number | null
  ) => {
    if (!activeCombination) return;
    const nextArtifacts = (activeCombination.artifacts ?? []).map((a) =>
      a.id === updated.id ? updated : a
    );
    updateCombinationArtifacts(activeCombination.id, nextArtifacts);
    const visuals = getSortedVisuals(updated);
    const keepId = preferredVisualId ?? selectedVisualId;
    const kept = keepId != null ? visuals.find((v) => v.id === keepId) : null;
    const nextSelected =
      kept?.id ??
      visuals.find((v) => v.is_primary)?.id ??
      visuals[0]?.id ??
      null;
    setSelectedVisualId(nextSelected);
  };

  const handleSetPrimaryVisual = async () => {
    if (!activeArtifact?.id || !selectedVisual?.id) return;
    if (selectedVisual.is_primary) {
      setVisualMenuOpen(false);
      return;
    }
    const visualId = selectedVisual.id;
    setError(null);
    setIsSettingPrimaryVisual(true);
    setVisualMenuOpen(false);
    try {
      const response = await api<{ message: string; artifact: Artifact }>(
        `/api/artifacts/${activeArtifact.id}/visuals/${visualId}/set_primary/`,
        { method: 'POST' }
      );
      updateActiveArtifactFromResponse(response.artifact, visualId);
    } catch (e: any) {
      setError(e.message || 'Failed to set primary visual');
    } finally {
      setIsSettingPrimaryVisual(false);
    }
  };

  const handleToggleFavoriteVisual = async () => {
    if (!activeArtifact?.id || !selectedVisual?.id) return;
    const visualId = selectedVisual.id;
    setError(null);
    setIsTogglingFavoriteVisual(true);
    setVisualMenuOpen(false);
    try {
      const response = await api<{ message: string; artifact: Artifact }>(
        `/api/artifacts/${activeArtifact.id}/visuals/${visualId}/toggle_favorite/`,
        { method: 'POST' }
      );
      updateActiveArtifactFromResponse(response.artifact, visualId);
    } catch (e: any) {
      setError(e.message || 'Failed to update visual favorite');
    } finally {
      setIsTogglingFavoriteVisual(false);
    }
  };

  const openDeleteVisualModal = () => {
    if (!selectedVisual) return;
    setVisualMenuOpen(false);
    setDeleteVisualTarget(selectedVisual);
  };

  const closeDeleteVisualModal = () => {
    if (isDeletingVisual) return;
    setDeleteVisualTarget(null);
  };

  const confirmDeleteVisual = async () => {
    if (!activeArtifact?.id || !deleteVisualTarget?.id) return;
    setError(null);
    setIsDeletingVisual(true);
    try {
      const response = await api<{ message: string; artifact: Artifact }>(
        `/api/artifacts/${activeArtifact.id}/visuals/${deleteVisualTarget.id}/`,
        { method: 'DELETE' }
      );
      updateActiveArtifactFromResponse(response.artifact);
      setDeleteVisualTarget(null);
    } catch (e: any) {
      setError(e.message || 'Failed to delete visual');
    } finally {
      setIsDeletingVisual(false);
    }
  };

  const openVisualEditPanel = () => {
    if (!selectedVisual) return;
    if (!canAiGenerate) {
      setError('You do not have permission to edit visuals');
      return;
    }
    setVisualMenuOpen(false);
    setShowVisualEditPanel(true);
  };

  const closeVisualEditPanel = () => {
    if (isApplyingVisualEdit) return;
    setShowVisualEditPanel(false);
    setVisualEditRequest('');
  };

  const handleApplyVisualEdit = async () => {
    if (!activeArtifact?.id || !selectedVisual?.id) return;
    if (!canAiGenerate) {
      setError('You do not have permission to edit visuals');
      return;
    }
    const trimmed = visualEditRequest.trim();
    if (!trimmed) return;
    const sourceVisualId = selectedVisual.id;
    setError(null);
    setIsApplyingVisualEdit(true);
    setVisualMenuOpen(false);
    try {
      const response = await api<{
        message: string;
        artifact: Artifact;
        visual_id: number;
        tokens_deducted: number;
      }>(`/api/artifacts/${activeArtifact.id}/visuals/${sourceVisualId}/edit/`, {
        method: 'POST',
        body: JSON.stringify({ edit_request: trimmed }),
      });
      updateActiveArtifactFromResponse(response.artifact, response.visual_id);
      setShowVisualEditPanel(false);
      setVisualEditRequest('');
    } catch (e: any) {
      setError(e.message || 'Failed to apply visual edit');
    } finally {
      setIsApplyingVisualEdit(false);
    }
  };

  const handleCopyWrittenConcept = async () => {
    if (!activeArtifact?.written_concept) return;
    try {
      await navigator.clipboard.writeText(activeArtifact.written_concept);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const openRenameCombinationModal = (combo: RecipeCombinationWithArtifacts) => {
    setComboMenuOpen(null);
    setRenameCombinationTarget(combo);
    setRenameCombinationValue(combo.combination_name);
  };

  const closeRenameCombinationModal = () => {
    if (isRenamingCombination) return;
    setRenameCombinationTarget(null);
    setRenameCombinationValue('');
  };

  const confirmRenameCombination = async () => {
    const combo = renameCombinationTarget;
    const name = renameCombinationValue.trim();
    if (!combo || !name || name === combo.combination_name) return;
    setError(null);
    setIsRenamingCombination(true);
    try {
      const updated = await api<RecipeCombinationWithArtifacts>(
        `/api/blueprints/${blueprintId}/recipe-combinations/${combo.id}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({ combination_name: name }),
        }
      );
      setCombinations((prev) =>
        prev.map((c) => (c.id === combo.id ? { ...c, ...updated, artifacts: c.artifacts } : c))
      );
      setRenameCombinationTarget(null);
      setRenameCombinationValue('');
    } catch (e: any) {
      setError(e.message || 'Failed to rename combination');
    } finally {
      setIsRenamingCombination(false);
    }
  };

  const openDeleteCombinationModal = (combo: RecipeCombinationWithArtifacts) => {
    setComboMenuOpen(null);
    setDeleteCombinationTarget(combo);
  };

  const closeDeleteCombinationModal = () => {
    if (isDeletingCombination) return;
    setDeleteCombinationTarget(null);
  };

  const confirmDeleteCombination = async () => {
    const combo = deleteCombinationTarget;
    if (!combo) return;
    setError(null);
    setIsDeletingCombination(true);
    try {
      await api(`/api/blueprints/${blueprintId}/recipe-combinations/${combo.id}/`, {
        method: 'DELETE',
      });
      const remaining = combinations.filter((c) => c.id !== combo.id);
      setCombinations(remaining);
      if (remaining.length) {
        const next = remaining[0];
        setActiveCombinationId(next.id);
        setIsEditMode(false);
        const sorted = [...(next.artifacts ?? [])].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setActiveArtifactId(sorted[sorted.length - 1]?.id ?? null);
      } else {
        setActiveCombinationId(null);
        setActiveArtifactId(null);
      }
      setDeleteCombinationTarget(null);
    } catch (e: any) {
      setError(e.message || 'Failed to delete combination');
    } finally {
      setIsDeletingCombination(false);
    }
  };

  const rootClassName = embedded
    ? 'flex-1 min-h-0 flex flex-col overflow-hidden text-gray-900'
    : 'absolute inset-0 z-[13000] bg-gray-50 text-gray-900 flex flex-col overflow-hidden';

  const combinationTabs = (
    <div className="flex items-end gap-1 overflow-x-auto border-b border-gray-200 pb-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {combinations.map((combo) => {
            const isActive = combo.id === activeCombinationId;
            return (
              <div key={combo.id} className="relative flex-shrink-0">
                <div
                  className={`flex items-stretch ${
                    isActive ? primaryTabActive : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleCombinationChange(combo.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                      isActive ? '' : primaryTabInactive
                    }`}
                  >
                    {combo.combination_name}
                  </button>
                  {isActive && (
                    <button
                      ref={comboMenuRef}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setComboMenuOpen(comboMenuOpen === combo.id ? null : combo.id);
                      }}
                      className="px-2 py-2.5 border-l border-gray-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {isActive && (
                  <DropdownMenu
                    open={comboMenuOpen === combo.id}
                    onClose={() => setComboMenuOpen(null)}
                    anchorRef={comboMenuRef}
                  >
                    <MenuItem onClick={() => openRenameCombinationModal(combo)}>
                      <span className="inline-flex items-center gap-2">
                        <Pencil className="w-3.5 h-3.5" />
                        Rename
                      </span>
                    </MenuItem>
                    <MenuItem destructive onClick={() => openDeleteCombinationModal(combo)}>
                      <span className="inline-flex items-center gap-2">
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete combination
                      </span>
                    </MenuItem>
                  </DropdownMenu>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={onBack}
            className="flex-shrink-0 mb-1.5 ml-2 inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-white px-4 py-1.5 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New combination
          </button>
    </div>
  );

  const panelWrapperClass = embedded
    ? 'flex-1 min-h-0 flex flex-col'
    : 'flex-1 min-h-0 px-8 pb-6 flex flex-col';

  const statusPicker = (
    <div className="relative mb-5" ref={statusMenuContainerRef}>
      <button
        type="button"
        onClick={() => {
          setStatusMenuOpen((open) => !open);
          setStatusInput(activeArtifact?.status || '');
        }}
        className="rounded-full border border-[#D1D1D1] bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
      >
        {activeArtifact?.status?.trim() || 'Select status'}
      </button>
      {statusMenuOpen ? (
        <div className="absolute left-0 top-full mt-2 z-20 w-64 rounded-xl border border-[#D1D1D1] bg-white shadow-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500">Select status</span>
            <button
              type="button"
              onClick={closeStatusMenu}
              className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Close status picker"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <input
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            placeholder="Type a custom status"
            className="w-full rounded-lg border border-[#D1D1D1] px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#3191C8]/30"
          />
          {usedArtifactStatuses.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {usedArtifactStatuses.map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => void handleSaveStatus(status)}
                  className="rounded-full bg-[#F5F5F5] px-2.5 py-0.5 text-xs text-gray-700 hover:bg-[#EAEAEA]"
                >
                  {status}
                </button>
              ))}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => void handleSaveStatus(statusInput)}
            disabled={isSavingStatus || !statusInput.trim()}
            className="w-full rounded-full bg-[#655FA8] text-white text-sm font-medium py-1.5 hover:bg-[#544fa0] disabled:opacity-50"
          >
            {isSavingStatus ? 'Saving...' : 'Apply status'}
          </button>
        </div>
      ) : null}
    </div>
  );

  if (isLoading) {
    return (
      <div className={embedded ? 'flex-1 flex items-center justify-center' : rootClassName}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      {!hidePageHeader && (
        <div className="flex-shrink-0 px-8 pt-6">
          <h1 className="text-lg font-semibold text-gray-900 mb-4">Concept Generation</h1>
          {!hideCombinationTabs ? combinationTabs : null}
        </div>
      )}
      {hidePageHeader && !hideCombinationTabs ? (
        <div className="flex-shrink-0 px-8 pt-4">{combinationTabs}</div>
      ) : null}

      {/* Combination panel — connects to active combination tab */}
      <div className={panelWrapperClass}>
        <div
          className={`flex-1 min-h-0 bg-white border border-gray-200 shadow-sm flex flex-col overflow-hidden ${
            activeCombinationId ? 'border-t-0 rounded-b-2xl' : 'rounded-2xl'
          }`}
        >
          {!activeCombination ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
              <p className="text-gray-600 mb-6">
                {embedded
                  ? 'Save a combination on the Recipe card view before generating product ideas.'
                  : 'Save a combination on the recipe page before generating concepts.'}
              </p>
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  {embedded ? 'Switch to Recipe card' : 'Go to recipe'}
                </button>
              ) : null}
            </div>
          ) : (
            <>
              {!embedded ? (
                <div className="flex-shrink-0 px-8 pt-6 pb-2">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1">
                    {activeCombination.combination_name}
                  </h2>
                  {onBack ? (
                    <button
                      type="button"
                      onClick={onBack}
                      className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Back to recipe
                    </button>
                  ) : null}
                </div>
              ) : null}

              {error && (
                <div className="mx-8 mb-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
                  {error}
                </div>
              )}

              {/* Artifact tabs — nested inside combination panel */}
              {showArtifactTabs ? (
              <div className="flex-shrink-0 px-8">
                <div className="flex items-end gap-1 border-b border-[#D1D1D1] overflow-y-hidden pb-0">
                  {orderedArtifacts.map((artifact, index) => {
                      const isActive = artifact.id === activeArtifactId;
                      return (
                        <div key={artifact.id} className="relative flex-shrink-0 flex items-end">
                          <div
                            className={`flex items-center rounded-t-lg border text-sm transition-colors ${
                              isActive
                                ? recipeTabActiveClass
                                : `${recipeTabInactiveClass} border hover:border-[#D1D1D1]`
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setActiveArtifactId(artifact.id);
                                setIsEditMode(false);
                                setIsManualAddMode(false);
                                setArtifactMenuOpen(null);
                              }}
                              className="px-4 py-2.5 outline-none focus-visible:ring-2 focus-visible:ring-[#3191C8]/30 rounded-tl-lg"
                            >
                              {getArtifactDisplayName(artifact, index)}
                            </button>
                            {isActive ? (
                              <button
                                ref={artifactMenuRef}
                                type="button"
                                onClick={() =>
                                  setArtifactMenuOpen(
                                    artifactMenuOpen === artifact.id ? null : artifact.id
                                  )
                                }
                                className="p-1.5 rounded text-gray-500 hover:bg-gray-100 pr-1"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            ) : null}
                          </div>
                          {isActive ? (
                            <DropdownMenu
                              open={artifactMenuOpen === artifact.id}
                              onClose={() => setArtifactMenuOpen(null)}
                              anchorRef={artifactMenuRef}
                            >
                              <MenuItem onClick={() => setArtifactMenuOpen(null)}>
                                <span className="inline-flex items-center gap-2">
                                  <Share2 className="w-3.5 h-3.5" />
                                  Share
                                </span>
                              </MenuItem>
                              <MenuItem onClick={() => openRenameArtifactModal(artifact, index)}>
                                <span className="inline-flex items-center gap-2">
                                  <Pencil className="w-3.5 h-3.5" />
                                  Rename concept
                                </span>
                              </MenuItem>
                              <MenuItem onClick={() => setArtifactMenuOpen(null)}>
                                <span className="inline-flex items-center gap-2">
                                  <Pencil className="w-3.5 h-3.5" />
                                  Export
                                </span>
                              </MenuItem>
                              <div className="my-1 border-t border-gray-100" />
                              <MenuItem
                                destructive
                                onClick={() => openDeleteArtifactModal(artifact, index)}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Delete concept
                                </span>
                              </MenuItem>
                            </DropdownMenu>
                          ) : null}
                        </div>
                      );
                    })}

                  <button
                    type="button"
                    onClick={() => void handleCreateBlankArtifact()}
                    disabled={!combinationReady || !canEditBlueprints || isCreatingBlankArtifact}
                    className="flex-shrink-0 mb-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#3191C8] bg-white text-[#3191C8] hover:bg-[#3191C8]/5 transition-colors disabled:opacity-50"
                    aria-label="Add concept"
                    title={
                      !canEditBlueprints
                        ? 'You do not have permission to edit blueprints'
                        : undefined
                    }
                  >
                    {isCreatingBlankArtifact ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
              ) : null}

              {/* Artifact content — connects to active artifact tab */}
              <div className="flex-1 min-h-0 overflow-auto bg-white border-t-0">
                {!activeArtifact ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-12 text-center">
                    {combinationReady && canEditBlueprints ? (
                      <>
                        <Loader2 className="w-8 h-8 text-[#3191C8] mb-4 animate-spin" />
                        <p className="text-sm text-gray-500">Opening product idea…</p>
                      </>
                    ) : (
                      <>
                        <Plus className="w-10 h-10 text-[#3191C8] mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No product ideas yet</h3>
                        <p className="text-sm text-gray-500 mb-6 max-w-md">
                          {!canEditBlueprints
                            ? 'You have view-only access. You can browse concepts but cannot add or edit them.'
                            : 'Complete all recipe card attribute selections before adding product ideas.'}
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] h-full min-h-[500px]">
                    {/* Left: written content */}
                    <div className="px-8 py-6 border-r border-gray-100 flex flex-col overflow-y-auto">
                      {isEditMode ? (
                        <div className="flex flex-col h-full">
                          <section className="mb-5 pb-5 border-b border-indigo-100">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">
                              Job to be done & tension statement
                            </h3>
                            <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                              <p>
                                {activeArtifact.job_to_be_done || blueprint?.job_to_be_done || '—'}
                              </p>
                              <p>{activeArtifact.tension || blueprint?.tension || '—'}</p>
                            </div>
                          </section>

                          <div className="flex-1 flex flex-col rounded-xl border border-indigo-200 bg-white overflow-hidden min-h-[420px] shadow-sm">
                            <div className="flex items-start gap-2.5 px-4 py-3 bg-indigo-50/60 border-b border-indigo-100">
                              <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-indigo-900/80 leading-relaxed">
                                You are editing the Catalyx AI generated concept narrative.
                                Changes made here may influence regenerated visual outputs.
                              </p>
                            </div>
                            <textarea
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                              className="flex-1 w-full min-h-[280px] text-sm text-gray-800 leading-relaxed resize-none border-0 p-4 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-200"
                            />
                            <div className="flex items-center gap-2 px-4 py-3 border-t border-indigo-100 bg-indigo-50/40">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditedContent(activeArtifact.written_concept);
                                  setIsEditMode(false);
                                }}
                                className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-4 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                disabled={isSavingEdit}
                                className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-1.5 text-sm font-medium text-white hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50"
                              >
                                {isSavingEdit ? 'Saving...' : 'Save changes'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : isManualAddMode ? (
                        <div className="flex flex-col h-full">
                          <div className="flex items-start justify-between gap-3 mb-4">
                            <h3 className="text-sm font-semibold text-gray-900">Written concept</h3>
                          </div>
                          <textarea
                            value={manualAddContent}
                            onChange={(e) => setManualAddContent(e.target.value)}
                            className="flex-1 w-full min-h-[280px] rounded-xl border border-[#D1D1D1] text-sm text-gray-800 leading-relaxed resize-none p-4 focus:outline-none focus:ring-2 focus:ring-[#3191C8]/30"
                            placeholder="Enter your written concept..."
                          />
                          <div className="flex items-center gap-2 mt-4">
                            <button
                              type="button"
                              onClick={() => {
                                setIsManualAddMode(false);
                                setManualAddContent('');
                              }}
                              className="rounded-full border border-[#3191C8] text-[#3191C8] px-4 py-1.5 text-sm font-medium hover:bg-[#3191C8]/5"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleSaveManualWritten()}
                              disabled={isSavingManual || !manualAddContent.trim()}
                              className="rounded-full bg-[#3191C8] text-white px-4 py-1.5 text-sm font-medium hover:bg-[#2a7fb0] disabled:opacity-50"
                            >
                              {isSavingManual ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : isArtifactBlank ? (
                        <>
                          {statusPicker}

                          <section className="mb-6">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Written concept</h3>
                            <div className="rounded-xl border border-dashed border-[#D1D1D1] bg-[#FAFAFA] p-8 text-center">
                              <p className="text-sm text-gray-500 mb-4">
                                Add a written concept manually or generate one with Catalyx AI.
                              </p>
                              <div className="flex items-center justify-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setManualAddContent('');
                                    setIsManualAddMode(true);
                                  }}
                                  disabled={!canGenerateConcepts}
                                  className="rounded-full border border-[#3191C8] text-[#3191C8] px-5 py-2 text-sm font-medium hover:bg-[#3191C8]/5 disabled:opacity-50"
                                >
                                  Add manually
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleGenerateWritten()}
                                  disabled={!canGenerateConcepts || isGeneratingWritten}
                                  className="rounded-full bg-[#655FA8] text-white px-5 py-2 text-sm font-medium hover:bg-[#544fa0] disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                  {isGeneratingWritten ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : null}
                                  Generate
                                </button>
                              </div>
                            </div>
                          </section>

                          <div className="mt-auto pt-4 border-t border-gray-100 text-xs text-gray-500 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <span>Added by: {activeArtifact.created_by_email?.trim() || '—'}</span>
                            <span>
                              Created on:{' '}
                              {new Date(activeArtifact.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                            <span>Market: {blueprint?.market?.trim() || '—'}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          {statusPicker}

                          <section className="mb-6">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <h3 className="text-sm font-semibold text-gray-900">Written concept</h3>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={handleCopyWrittenConcept}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                                  title="Copy"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditedContent(activeArtifact.written_concept);
                                    setIsEditMode(true);
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                                  title="Edit"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {activeArtifact.written_concept || '—'}
                            </div>
                          </section>

                          {copySuccess ? (
                            <p className="text-xs text-emerald-600 -mt-4 mb-4">Copied to clipboard</p>
                          ) : null}

                          <div className="mt-auto pt-4 border-t border-gray-100 text-xs text-gray-500 grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
                            <span>Added by: {activeArtifact.created_by_email?.trim() || '—'}</span>
                            <span>
                              Created on:{' '}
                              {new Date(activeArtifact.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                            <span>Market: {blueprint?.market?.trim() || '—'}</span>
                          </div>

                          {!embedded && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-start gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <h4 className="text-sm font-semibold text-gray-900">Methodology note</h4>
                            </div>
                            {methodologyFlags.length > 0 ? (
                              <>
                                <p className="text-xs text-gray-600 mb-2 ml-6">Includes:</p>
                                <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc list-inside">
                                  {methodologyFlags.map((flag, index) => (
                                    <li key={`${flag.type}-${index}`}>
                                      {flag.label} (User added)
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ) : (
                              <p className="text-sm text-gray-500 ml-6">
                                No user-added inputs flagged for this artifact.
                              </p>
                            )}
                          </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Right: visual */}
                    <div className="px-8 py-6 flex flex-col min-h-[400px]">
                      <div className="flex items-center justify-end gap-4 mb-4">
                        {hasVisual ? (
                          <>
                            <p className="text-sm font-semibold text-gray-900">
                              Overall product market fit score:
                            </p>
                            <div className="rounded-lg border border-[#D1D1D1] bg-white px-4 py-2 text-center min-w-[4.5rem]">
                              <div
                                className="text-xl font-bold leading-tight"
                                style={{ color: pmfColor }}
                              >
                                {pmfScoreDisplay}
                              </div>
                              <div
                                className="text-xs font-semibold mt-0.5"
                                style={{ color: pmfColor }}
                              >
                                {pmfLabelDisplay}
                              </div>
                            </div>
                            {onOpenProductIdeaValidator ? (
                              <button
                                type="button"
                                onClick={onOpenProductIdeaValidator}
                                className="rounded-full bg-[#655FA8] text-white px-5 py-2 text-sm font-medium hover:bg-[#544fa0] transition-colors"
                              >
                                Product Idea Validator
                              </button>
                            ) : null}
                          </>
                        ) : onOpenProductIdeaGenerator ? (
                          <button
                            type="button"
                            onClick={onOpenProductIdeaGenerator}
                            className="rounded-full bg-[#655FA8] text-white px-5 py-2 text-sm font-medium hover:bg-[#544fa0] transition-colors"
                          >
                            Product Idea Generator
                          </button>
                        ) : null}
                      </div>

                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Visual concept</h3>

                      {!hasVisual ? (
                        <div className="rounded-xl border border-dashed border-[#D1D1D1] bg-[#FAFAFA] p-8 text-center mb-4">
                          <p className="text-sm text-gray-500 mb-4">
                            Generate a product visual after adding a written concept.
                          </p>
                          <button
                            type="button"
                            onClick={() => void handleRegenerateVisual()}
                            disabled={
                              !canGenerateConcepts ||
                              !activeArtifact.written_concept?.trim() ||
                              isRegenerating
                            }
                            className="rounded-full bg-[#655FA8] text-white px-5 py-2 text-sm font-medium hover:bg-[#544fa0] disabled:opacity-50 inline-flex items-center gap-2"
                          >
                            {isRegenerating ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : null}
                            Generate
                          </button>
                        </div>
                      ) : null}

                      <div
                        className={`flex-1 relative rounded-xl border border-dashed border-gray-300 bg-gray-50 overflow-hidden min-h-[360px] ${
                          !hasVisual ? 'hidden' : ''
                        }`}
                      >
                        {selectedVisualUrl ? (
                          <img
                            src={selectedVisualUrl}
                            alt="Visual concept"
                            className="w-full h-full object-contain min-h-[360px]"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div
                              className="absolute inset-8 border border-gray-200 rounded-lg"
                              aria-hidden
                            >
                              <svg
                                className="w-full h-full text-gray-200"
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                              >
                                <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" strokeWidth="0.5" />
                                <line x1="100" y1="0" x2="0" y2="100" stroke="currentColor" strokeWidth="0.5" />
                              </svg>
                            </div>
                            <p className="relative text-sm text-gray-400">
                              Visual concept appears here
                            </p>
                          </div>
                        )}

                        {selectedVisual?.is_primary && (
                          <div className="absolute top-3 left-3 rounded-full border border-white/70 bg-black/50 px-2.5 py-1 text-xs font-medium text-white inline-flex items-center gap-1.5">
                            <Trophy className="w-3.5 h-3.5" />
                            Primary
                          </div>
                        )}
                        {selectedVisual?.is_favorite && (
                          <div
                            className={`absolute top-3 ${
                              selectedVisual?.is_primary ? 'left-[104px]' : 'left-3'
                            } rounded-full border border-white/70 bg-black/50 px-2.5 py-1 text-xs font-medium text-white inline-flex items-center gap-1`}
                          >
                            <Heart className="w-3 h-3 fill-current" />
                            Favourite
                          </div>
                        )}
                        {selectedVisual?.visual_source === 'user' && (
                          <div
                            className={`absolute ${
                              selectedVisual?.is_primary || selectedVisual?.is_favorite
                                ? 'top-12 left-3'
                                : 'top-3 left-3'
                            } rounded-full border border-white/70 bg-black/50 px-2.5 py-1 text-xs font-medium text-white inline-flex items-center gap-1.5`}
                          >
                            <Pencil className="w-3 h-3" />
                            {activeArtifact?.created_by_email?.trim()
                              ? `Edited by ${activeArtifact.created_by_email}`
                              : 'Edited by user'}
                          </div>
                        )}

                        {isApplyingVisualEdit && (
                          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75 backdrop-blur-sm">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
                            <p className="text-sm font-medium text-gray-900">Updating image...</p>
                          </div>
                        )}

                        <div className="absolute top-3 right-3">
                          <button
                            ref={visualMenuRef}
                            type="button"
                            onClick={() => setVisualMenuOpen((v) => !v)}
                            disabled={!selectedVisual || isVisualActionBusy}
                            className="p-1.5 rounded-lg bg-white text-gray-500 hover:text-gray-700 border border-gray-200 shadow-sm disabled:opacity-50"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          <DropdownMenu
                            open={visualMenuOpen}
                            onClose={() => setVisualMenuOpen(false)}
                            anchorRef={visualMenuRef}
                            align="right"
                          >
                            <MenuItem
                              onClick={() => void handleSetPrimaryVisual()}
                              disabled={selectedVisual?.is_primary}
                            >
                              <span className="inline-flex items-center gap-2">
                                <Trophy className="w-3.5 h-3.5" />
                                {selectedVisual?.is_primary ? 'Primary visual' : 'Set as primary'}
                              </span>
                            </MenuItem>
                            <MenuItem onClick={() => void handleToggleFavoriteVisual()}>
                              <span className="inline-flex items-center gap-2">
                                <Heart className="w-3.5 h-3.5" />
                                {selectedVisual?.is_favorite ? 'Remove favourite' : 'Mark as favourite'}
                              </span>
                            </MenuItem>
                            <MenuItem onClick={() => setVisualMenuOpen(false)}>
                              <span className="inline-flex items-center gap-2">
                                <Share2 className="w-3.5 h-3.5" />
                                Share
                              </span>
                            </MenuItem>
                            <MenuItem
                              onClick={openVisualEditPanel}
                              disabled={!canAiGenerate || !canEditBlueprints}
                            >
                              <span className="inline-flex items-center gap-2">
                                <Pencil className="w-3.5 h-3.5" />
                                Edit
                              </span>
                            </MenuItem>
                            <div className="my-1 border-t border-gray-100" />
                            <MenuItem destructive onClick={openDeleteVisualModal}>
                              <span className="inline-flex items-center gap-2">
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete image
                              </span>
                            </MenuItem>
                          </DropdownMenu>
                        </div>

                        <button
                          type="button"
                          onClick={handleRegenerateVisual}
                          disabled={
                            isVisualActionBusy ||
                            !activeArtifact.written_concept ||
                            !canAiGenerate ||
                            !canEditBlueprints
                          }
                          title={
                            !canEditBlueprints || !canAiGenerate
                              ? 'You do not have permission to regenerate visuals'
                              : undefined
                          }
                          className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors shadow-sm"
                        >
                          <RefreshCw
                            className={`w-3.5 h-3.5 ${isRegenerating ? 'animate-spin' : ''}`}
                          />
                          {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                        </button>
                      </div>

                      <div className={`mt-4 relative ${!hasVisual ? 'hidden' : ''}`}>
                        {showVisualEditPanel ? (
                          <div className="rounded-xl border border-gray-300 bg-white overflow-hidden shadow-sm">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                              <h3 className="text-sm font-semibold text-gray-900">Edit visual</h3>
                              <button
                                type="button"
                                onClick={closeVisualEditPanel}
                                disabled={isApplyingVisualEdit}
                                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                                aria-label="Close edit visual panel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="px-4 py-4">
                              <p className="text-sm text-gray-700 mb-3">
                                Describe the image adjustment you want to make.
                              </p>
                              <textarea
                                value={visualEditRequest}
                                onChange={(e) => setVisualEditRequest(e.target.value)}
                                disabled={isApplyingVisualEdit}
                                rows={4}
                                placeholder="Remove the coloured background and make the background white or transparent."
                                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:bg-gray-50 resize-none"
                              />
                              <div className="mt-3 flex items-start gap-2 text-xs text-gray-600">
                                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-500" />
                                <p>
                                  Concept changes like flavour, colour palette, or pack format must be
                                  updated in the recipe card.
                                </p>
                              </div>
                            </div>
                            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                              <button
                                type="button"
                                onClick={closeVisualEditPanel}
                                disabled={isApplyingVisualEdit}
                                className="rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleApplyVisualEdit()}
                                disabled={isApplyingVisualEdit || !visualEditRequest.trim()}
                                className="rounded-full bg-gray-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 inline-flex items-center gap-2"
                              >
                                {isApplyingVisualEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                                {isApplyingVisualEdit ? 'Updating image...' : 'Apply edit'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5">
                        {activeArtifactVisuals.length > 0 ? (
                          <>
                            {showVisualScrollArrows && (
                              <button
                                type="button"
                                onClick={() =>
                                  setVisualThumbnailOffset((offset) => Math.max(0, offset - 1))
                                }
                                disabled={!canScrollVisualsLeft}
                                className="flex-shrink-0 rounded-full border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label="Show previous visuals"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                            )}
                            <div
                              className="overflow-hidden"
                              style={{
                                width: showVisualScrollArrows
                                  ? thumbnailViewportWidth
                                  : activeArtifactVisuals.length * THUMBNAIL_SIZE_PX +
                                    (activeArtifactVisuals.length - 1) * THUMBNAIL_GAP_PX,
                              }}
                            >
                              <div
                                className="flex transition-transform duration-300 ease-out"
                                style={{
                                  gap: THUMBNAIL_GAP_PX,
                                  transform: `translateX(-${
                                    visualThumbnailOffset * (THUMBNAIL_SIZE_PX + THUMBNAIL_GAP_PX)
                                  }px)`,
                                }}
                              >
                                {activeArtifactVisuals.map((visual) => (
                                  <button
                                    key={visual.id}
                                    type="button"
                                    onClick={() => setSelectedVisualId(visual.id)}
                                    className={`relative flex-shrink-0 overflow-hidden rounded-md border transition ${
                                      selectedVisual?.id === visual.id
                                        ? 'border-indigo-500 ring-2 ring-indigo-200'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    style={{
                                      width: THUMBNAIL_SIZE_PX,
                                      height: THUMBNAIL_SIZE_PX,
                                    }}
                                  >
                                    <img
                                      src={visual.image_url}
                                      alt="Visual variation thumbnail"
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute top-0.5 left-0.5 flex items-center gap-0.5">
                                      {visual.is_primary && (
                                        <span className="rounded bg-black/60 p-0.5 text-amber-300">
                                          <Trophy className="w-2.5 h-2.5" />
                                        </span>
                                      )}
                                      {visual.is_favorite && (
                                        <span className="rounded bg-black/60 p-px text-white">
                                          <Heart className="w-2 h-2 fill-current" />
                                        </span>
                                      )}
                                      {visual.visual_source === 'user' && (
                                        <span className="rounded bg-black/60 p-0.5 text-white">
                                          <Pencil className="w-2.5 h-2.5" />
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                            {showVisualScrollArrows && (
                              <button
                                type="button"
                                onClick={() =>
                                  setVisualThumbnailOffset((offset) =>
                                    Math.min(maxVisualThumbnailOffset, offset + 1)
                                  )
                                }
                                disabled={!canScrollVisualsRight}
                                className="flex-shrink-0 rounded-full border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label="Show next visuals"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          <div className="w-full rounded-lg border border-dashed border-gray-300 bg-white px-3 py-4 text-center text-xs text-gray-500">
                            Regenerated visuals will appear here as selectable thumbnails.
                          </div>
                        )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {deleteVisualTarget && (
        <div
          className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-visual-title"
          onClick={closeDeleteVisualModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
              <h2 id="delete-visual-title" className="text-lg font-semibold text-gray-900">
                Delete image?
              </h2>
              <button
                type="button"
                onClick={closeDeleteVisualModal}
                disabled={isDeletingVisual}
                className="p-2 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-gray-600 leading-relaxed pt-2">
                This will permanently remove the selected visual from this artifact.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={closeDeleteVisualModal}
                disabled={isDeletingVisual}
                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteVisual()}
                disabled={isDeletingVisual}
                className="rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isDeletingVisual && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {renameArtifactTarget && (
        <div
          className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rename-artifact-title"
          onClick={closeRenameArtifactModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
              <h2 id="rename-artifact-title" className="text-lg font-semibold text-gray-900">
                Rename concept
              </h2>
              <button
                type="button"
                onClick={closeRenameArtifactModal}
                disabled={isRenamingArtifact}
                className="p-2 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-6">
              <label htmlFor="rename-artifact-input" className="sr-only">
                Artifact name
              </label>
              <input
                id="rename-artifact-input"
                type="text"
                value={renameArtifactValue}
                onChange={(e) => setRenameArtifactValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void confirmRenameArtifact();
                  }
                }}
                disabled={isRenamingArtifact}
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:bg-gray-50"
                placeholder="Concept name"
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={closeRenameArtifactModal}
                disabled={isRenamingArtifact}
                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmRenameArtifact()}
                disabled={
                  isRenamingArtifact ||
                  !renameArtifactValue.trim() ||
                  renameArtifactValue.trim() ===
                    getArtifactDisplayName(
                      renameArtifactTarget.artifact,
                      renameArtifactTarget.index
                    )
                }
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2 text-sm font-semibold text-white hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isRenamingArtifact && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteArtifactTarget && (
        <div
          className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-artifact-title"
          onClick={closeDeleteArtifactModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
              <h2 id="delete-artifact-title" className="text-lg font-semibold text-gray-900">
                Delete concept?
              </h2>
              <button
                type="button"
                onClick={closeDeleteArtifactModal}
                disabled={isDeletingArtifact}
                className="p-2 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-gray-600 leading-relaxed pt-2">
                Delete &ldquo;
                {getArtifactDisplayName(
                  deleteArtifactTarget.artifact,
                  deleteArtifactTarget.index
                )}
                &rdquo;? This will permanently remove the artifact and its visual concept. This
                cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={closeDeleteArtifactModal}
                disabled={isDeletingArtifact}
                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteArtifact()}
                disabled={isDeletingArtifact}
                className="rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isDeletingArtifact && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {renameCombinationTarget && (
        <div
          className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rename-combination-title"
          onClick={closeRenameCombinationModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
              <h2 id="rename-combination-title" className="text-lg font-semibold text-gray-900">
                Rename combination
              </h2>
              <button
                type="button"
                onClick={closeRenameCombinationModal}
                disabled={isRenamingCombination}
                className="p-2 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-6">
              <label htmlFor="rename-combination-input" className="sr-only">
                Combination name
              </label>
              <input
                id="rename-combination-input"
                type="text"
                value={renameCombinationValue}
                onChange={(e) => setRenameCombinationValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void confirmRenameCombination();
                  }
                }}
                disabled={isRenamingCombination}
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:bg-gray-50"
                placeholder="Combination name"
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={closeRenameCombinationModal}
                disabled={isRenamingCombination}
                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmRenameCombination()}
                disabled={
                  isRenamingCombination ||
                  !renameCombinationValue.trim() ||
                  renameCombinationValue.trim() === renameCombinationTarget.combination_name
                }
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2 text-sm font-semibold text-white hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isRenamingCombination && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteCombinationTarget && (
        <div
          className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-combination-title"
          onClick={closeDeleteCombinationModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
              <h2 id="delete-combination-title" className="text-lg font-semibold text-gray-900">
                Delete combination?
              </h2>
              <button
                type="button"
                onClick={closeDeleteCombinationModal}
                disabled={isDeletingCombination}
                className="p-2 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-gray-600 leading-relaxed pt-2">
                Delete &ldquo;{deleteCombinationTarget.combination_name}&rdquo;? This cannot be
                undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={closeDeleteCombinationModal}
                disabled={isDeletingCombination}
                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteCombination()}
                disabled={isDeletingCombination}
                className="rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isDeletingCombination && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenerateConfirmModal && (
        <div
          className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="generate-variation-title"
          onClick={() => setShowGenerateConfirmModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
              <h2 id="generate-variation-title" className="text-lg font-semibold text-gray-900">
                Generate new variation?
              </h2>
              <button
                type="button"
                onClick={() => setShowGenerateConfirmModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-6 pb-6 text-sm text-gray-600 leading-relaxed">
              Creates a new visual and written artifact.
            </p>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setShowGenerateConfirmModal(false)}
                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerateVariation}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2 text-sm font-semibold text-white hover:from-indigo-700 hover:to-violet-700"
              >
                Generate variation
              </button>
            </div>
          </div>
        </div>
      )}

      {isGenerating && (
        <div
          className="fixed inset-0 z-[14100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
          role="status"
          aria-live="polite"
          aria-label="Generating variation"
        >
          <div className="w-full max-w-sm rounded-2xl border border-indigo-200 bg-white shadow-2xl px-8 py-10 flex flex-col items-center text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-4" />
            <p className="text-base font-semibold text-gray-900">Generating variation...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConceptGenerationPage;
